import type { DocumentNode } from "graphql"

import { RequestLeaveStatus } from "__generated__/graphql"
import { useMutation } from "@apollo/client"
import { LoadingButton } from "@mui/lab"
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material"
import { DialogCloseButton } from "components/common"
import { useSnackbar } from "notistack"
import { APPROVE_REQUEST_LEAVE, FIND_REQUEST_LEAVES } from "views/RequestLeave/graphql"

type ApproverFormProps = {
  actionType: "approve" | "reject"
  onClose: () => void
  open: boolean
  refetchQueries?: DocumentNode[]
  selectedLeave: { id: number }
}

const ApproverForm = ({ actionType, onClose, open, refetchQueries, selectedLeave }: ApproverFormProps) => {
  const { enqueueSnackbar } = useSnackbar()
  const [approveRequestLeave, { loading }] = useMutation(APPROVE_REQUEST_LEAVE)

  const handleConfirm = async () => {
    const status = actionType === "approve" ? RequestLeaveStatus.Approved : RequestLeaveStatus.Rejected

    try {
      await approveRequestLeave({
        refetchQueries: refetchQueries ?? [FIND_REQUEST_LEAVES],
        variables: {
          id: selectedLeave.id,
          status,
        },
      })
      enqueueSnackbar(`Đã ${actionType === "approve" ? "phê duyệt" : "từ chối"} thành công!`, {
        variant: "success",
      })
      onClose()
    } catch {
      enqueueSnackbar(`Không thể ${actionType === "approve" ? "phê duyệt" : "từ chối"} yêu cầu.`, {
        variant: "error",
      })
    }
  }

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle>
        {actionType === "approve" ? "Xác nhận phê duyệt" : "Xác nhận từ chối"}
        <DialogCloseButton aria-label="Đóng" onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText color="text.secondary">
          {actionType === "approve"
            ? "Bạn có chắc muốn phê duyệt yêu cầu này?"
            : "Bạn có chắc muốn từ chối yêu cầu này?"}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <LoadingButton color="inherit" disabled={loading} onClick={onClose} variant="outlined">
          Hủy
        </LoadingButton>
        <LoadingButton
          color={actionType === "approve" ? "success" : "error"}
          loading={loading}
          onClick={handleConfirm}
          variant="contained"
        >
          Xác nhận
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ApproverForm
